/**
 * This file was auto-generated by Fern from our API Definition.
 */

import * as core from '../../../../core';
import { mergeHeaders, mergeOnlyDefinedHeaders } from '../../../../core/headers';
import * as environments from '../../../../environments';
import * as errors from '../../../../errors/index';
import * as serializers from '../../../../serialization/index';
import * as Truvity from '../../../index';

export declare namespace Dids {
    export interface Options {
        environment?: core.Supplier<environments.TruvityEnvironment | string>;
        /** Specify a custom URL to connect the client to. */
        baseUrl?: core.Supplier<string>;
        apiKey?: core.Supplier<string | undefined>;
        /** Additional headers to include in requests. */
        headers?: Record<string, string | core.Supplier<string | undefined> | undefined>;
        fetcher?: core.FetchFunction;
    }

    export interface RequestOptions {
        /** The maximum time to wait for a response in seconds. */
        timeoutInSeconds?: number;
        /** The number of times to retry the request. Defaults to 2. */
        maxRetries?: number;
        /** A hook to abort the request. */
        abortSignal?: AbortSignal;
        /** Additional query string parameters to include in the request. */
        queryParams?: Record<string, unknown>;
        /** Additional headers to include in the request. */
        headers?: Record<string, string | core.Supplier<string | undefined> | undefined>;
    }
}

/**
 * The API to manage DID Documents.
 */
export class Dids {
    protected readonly _options: Dids.Options;

    constructor(_options: Dids.Options = {}) {
        this._options = _options;
    }

    /**
     * Return the tenant's DID(did:web) document.
     *         Comparing to [DIDDocumentGet](#tag/desk/operation/DIDDocumentGet) endpoint, this one returns the tenant's DID(did:web) document for the authenticated tenant only.
     *
     * @param {Truvity.DidDocumentSelfGetRequest} request
     * @param {Dids.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.dids.didDocumentSelfGet()
     */
    public didDocumentSelfGet(
        request: Truvity.DidDocumentSelfGetRequest = {},
        requestOptions?: Dids.RequestOptions,
    ): core.HttpResponsePromise<Truvity.DidDocument> {
        return core.HttpResponsePromise.fromPromise(this.__didDocumentSelfGet(request, requestOptions));
    }

    private async __didDocumentSelfGet(
        request: Truvity.DidDocumentSelfGetRequest = {},
        requestOptions?: Dids.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.DidDocument>> {
        const { ifNoneMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'If-None-Match': ifNoneMatch != null ? ifNoneMatch : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                'dids/self',
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.DidDocument.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling GET /dids/self.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    protected async _getCustomAuthorizationHeaders() {
        const apiKeyValue = await core.Supplier.get(this._options.apiKey);
        return { 'X-API-KEY': apiKeyValue };
    }
}
